/**
 * Notification Service
 * Centralized notification management system for the application
 */

// Notification Types
export enum NotificationType {
  SUCCESS = "success",
  ERROR = "error",
  WARNING = "warning",
  INFO = "info",
}

// Notification Categories
export enum NotificationCategory {
  FILE_UPLOAD = "file_upload",
  FILE_PROCESS = "file_process",
  EXTRACTION = "extraction",
  APPROVAL = "approval",
  REJECTION = "rejection",
  SYSTEM = "system",
  VALIDATION = "validation",
  EXPORT = "export",
  DELETE = "delete",
  UPDATE = "update",
}

// Notification Data Model Interface
export interface INotification {
  id: string;
  type: NotificationType;
  category: NotificationCategory;
  title: string;
  description: string;
  createdAt: string; // ISO 8601 format
  moreDetails?: string | null;
  fileName?: string | null;
  fileId?: string | null;
  metadata?: Record<string, any>;
  isRead?: boolean;
  isDismissed?: boolean;
}

// Notification Builder Class
export class NotificationBuilder {
  private notification: INotification;

  constructor() {
    this.notification = {
      id: this.generateId(),
      type: NotificationType.INFO,
      category: NotificationCategory.SYSTEM,
      title: "",
      description: "",
      createdAt: new Date().toISOString(),
      isRead: false,
      isDismissed: false,
    };
  }

  private generateId(): string {
    return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  setType(type: NotificationType): NotificationBuilder {
    this.notification.type = type;
    return this;
  }

  setCategory(category: NotificationCategory): NotificationBuilder {
    this.notification.category = category;
    return this;
  }

  setTitle(title: string): NotificationBuilder {
    this.notification.title = title;
    return this;
  }

  setDescription(description: string): NotificationBuilder {
    this.notification.description = description;
    return this;
  }

  setMoreDetails(details: string | null): NotificationBuilder {
    this.notification.moreDetails = details;
    return this;
  }

  setFileName(fileName: string): NotificationBuilder {
    this.notification.fileName = fileName;
    return this;
  }

  setFileId(fileId: string): NotificationBuilder {
    this.notification.fileId = fileId;
    return this;
  }

  setMetadata(metadata: Record<string, any>): NotificationBuilder {
    this.notification.metadata = metadata;
    return this;
  }

  setCreatedAt(date: Date | string): NotificationBuilder {
    this.notification.createdAt = typeof date === "string" ? date : date.toISOString();
    return this;
  }

  build(): INotification {
    return { ...this.notification };
  }
}

// Notification Service - Main utility functions
export class NotificationService {
  /**
   * Create a new notification using the builder pattern
   */
  static create(): NotificationBuilder {
    return new NotificationBuilder();
  }

  /**
   * Quick notification creators for common scenarios
   */

  // File Upload Notifications
  static fileUploadSuccess(fileName: string, fileId?: string, details?: string): INotification {
    return new NotificationBuilder()
      .setType(NotificationType.SUCCESS)
      .setCategory(NotificationCategory.FILE_UPLOAD)
      .setTitle("File Uploaded Successfully")
      .setDescription(`${fileName} has been uploaded and is ready for processing.`)
      .setFileName(fileName)
      .setFileId(fileId || "")
      .setMoreDetails(details || null)
      .build();
  }

  static fileUploadError(fileName: string, errorMessage: string): INotification {
    return new NotificationBuilder()
      .setType(NotificationType.ERROR)
      .setCategory(NotificationCategory.FILE_UPLOAD)
      .setTitle("File Upload Failed")
      .setDescription(`Failed to upload ${fileName}.`)
      .setFileName(fileName)
      .setMoreDetails(errorMessage)
      .build();
  }

  // File Processing Notifications
  static fileProcessingStarted(fileName: string, fileId?: string): INotification {
    return new NotificationBuilder()
      .setType(NotificationType.INFO)
      .setCategory(NotificationCategory.FILE_PROCESS)
      .setTitle("Processing Started")
      .setDescription(`Processing has started for ${fileName}.`)
      .setFileName(fileName)
      .setFileId(fileId || "")
      .build();
  }

  static fileProcessingComplete(
    fileName: string,
    fileId?: string,
    details?: string
  ): INotification {
    return new NotificationBuilder()
      .setType(NotificationType.SUCCESS)
      .setCategory(NotificationCategory.FILE_PROCESS)
      .setTitle("Processing Complete")
      .setDescription(`${fileName} has been processed successfully.`)
      .setFileName(fileName)
      .setFileId(fileId || "")
      .setMoreDetails(details || null)
      .build();
  }

  static fileProcessingError(fileName: string, errorMessage: string): INotification {
    return new NotificationBuilder()
      .setType(NotificationType.ERROR)
      .setCategory(NotificationCategory.FILE_PROCESS)
      .setTitle("Processing Failed")
      .setDescription(`Failed to process ${fileName}.`)
      .setFileName(fileName)
      .setMoreDetails(errorMessage)
      .build();
  }

  // Extraction Notifications
  static extractionSuccess(fileName: string, formCount?: number): INotification {
    const description = formCount
      ? `Extracted ${formCount} form(s) from ${fileName}.`
      : `Data extraction completed for ${fileName}.`;

    return new NotificationBuilder()
      .setType(NotificationType.SUCCESS)
      .setCategory(NotificationCategory.EXTRACTION)
      .setTitle("Extraction Complete")
      .setDescription(description)
      .setFileName(fileName)
      .setMetadata({ formCount })
      .build();
  }

  static extractionError(fileName: string, errorMessage: string): INotification {
    return new NotificationBuilder()
      .setType(NotificationType.ERROR)
      .setCategory(NotificationCategory.EXTRACTION)
      .setTitle("Extraction Failed")
      .setDescription(`Failed to extract data from ${fileName}.`)
      .setFileName(fileName)
      .setMoreDetails(errorMessage)
      .build();
  }

  // Approval/Review Notifications
  static approvalSuccess(fileName: string, approvedBy?: string): INotification {
    return new NotificationBuilder()
      .setType(NotificationType.SUCCESS)
      .setCategory(NotificationCategory.APPROVAL)
      .setTitle("File Approved")
      .setDescription(`${fileName} has been approved successfully.`)
      .setFileName(fileName)
      .setMoreDetails(approvedBy ? `Approved by: ${approvedBy}` : null)
      .build();
  }

  static rejectionNotification(
    fileName: string,
    reason: string,
    rejectedBy?: string
  ): INotification {
    return new NotificationBuilder()
      .setType(NotificationType.WARNING)
      .setCategory(NotificationCategory.REJECTION)
      .setTitle("File Rejected")
      .setDescription(`${fileName} has been rejected.`)
      .setFileName(fileName)
      .setMoreDetails(`Reason: ${reason}${rejectedBy ? ` | Rejected by: ${rejectedBy}` : ""}`)
      .build();
  }

  // Export Notifications
  static exportSuccess(fileName: string, format: string): INotification {
    return new NotificationBuilder()
      .setType(NotificationType.SUCCESS)
      .setCategory(NotificationCategory.EXPORT)
      .setTitle("Export Successful")
      .setDescription(`${fileName} has been exported as ${format}.`)
      .setFileName(fileName)
      .setMetadata({ format })
      .build();
  }

  static exportError(fileName: string, errorMessage: string): INotification {
    return new NotificationBuilder()
      .setType(NotificationType.ERROR)
      .setCategory(NotificationCategory.EXPORT)
      .setTitle("Export Failed")
      .setDescription(`Failed to export ${fileName}.`)
      .setFileName(fileName)
      .setMoreDetails(errorMessage)
      .build();
  }

  // Delete Notifications
  static deleteSuccess(fileName: string): INotification {
    return new NotificationBuilder()
      .setType(NotificationType.SUCCESS)
      .setCategory(NotificationCategory.DELETE)
      .setTitle("File Deleted")
      .setDescription(`${fileName} has been deleted successfully.`)
      .setFileName(fileName)
      .build();
  }

  static deleteError(fileName: string, errorMessage: string): INotification {
    return new NotificationBuilder()
      .setType(NotificationType.ERROR)
      .setCategory(NotificationCategory.DELETE)
      .setTitle("Delete Failed")
      .setDescription(`Failed to delete ${fileName}.`)
      .setFileName(fileName)
      .setMoreDetails(errorMessage)
      .build();
  }

  // Validation Notifications
  static validationWarning(title: string, description: string, details?: string): INotification {
    return new NotificationBuilder()
      .setType(NotificationType.WARNING)
      .setCategory(NotificationCategory.VALIDATION)
      .setTitle(title)
      .setDescription(description)
      .setMoreDetails(details || null)
      .build();
  }

  static validationError(title: string, description: string, details?: string): INotification {
    return new NotificationBuilder()
      .setType(NotificationType.ERROR)
      .setCategory(NotificationCategory.VALIDATION)
      .setTitle(title)
      .setDescription(description)
      .setMoreDetails(details || null)
      .build();
  }

  // System Notifications
  static systemInfo(title: string, description: string, details?: string): INotification {
    return new NotificationBuilder()
      .setType(NotificationType.INFO)
      .setCategory(NotificationCategory.SYSTEM)
      .setTitle(title)
      .setDescription(description)
      .setMoreDetails(details || null)
      .build();
  }

  static systemError(title: string, description: string, details?: string): INotification {
    return new NotificationBuilder()
      .setType(NotificationType.ERROR)
      .setCategory(NotificationCategory.SYSTEM)
      .setTitle(title)
      .setDescription(description)
      .setMoreDetails(details || null)
      .build();
  }

  // Update Notifications
  static updateSuccess(fileName: string, details?: string): INotification {
    return new NotificationBuilder()
      .setType(NotificationType.SUCCESS)
      .setCategory(NotificationCategory.UPDATE)
      .setTitle("Update Successful")
      .setDescription(`${fileName} has been updated successfully.`)
      .setFileName(fileName)
      .setMoreDetails(details || null)
      .build();
  }

  /**
   * Format notification date for display
   */
  static formatNotificationDate(createdAt: string): {
    formattedDate: string;
    formattedTime: string;
  } {
    if (!createdAt) {
      return { formattedDate: "Just now", formattedTime: "" };
    }

    const date = new Date(createdAt);

    if (isNaN(date.getTime())) {
      console.warn("Invalid notification timestamp:", createdAt);
      return { formattedDate: "Just now", formattedTime: "" };
    }

    return {
      formattedDate: date.toLocaleDateString(),
      formattedTime: date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    };
  }

  /**
   * Convert old notification format to new format
   */
  static migrateOldNotification(oldNotification: any): INotification {
    return new NotificationBuilder()
      .setType(
        oldNotification.type === "success" ? NotificationType.SUCCESS : NotificationType.ERROR
      )
      .setCategory(NotificationCategory.SYSTEM)
      .setTitle(oldNotification.filename || "Notification")
      .setDescription(oldNotification.message || "")
      .setFileName(oldNotification.filename || null)
      .setCreatedAt(oldNotification.date || new Date().toISOString())
      .setMoreDetails(oldNotification.moreDetails || null)
      .build();
  }

  /**
   * Store notifications in localStorage
   */
  static saveToLocalStorage(notifications: INotification[]): void {
    try {
      localStorage.setItem("notifications_list", JSON.stringify(notifications));
    } catch (error) {
      console.error("Failed to save notifications to localStorage:", error);
    }
  }

  /**
   * Load notifications from localStorage
   */
  static loadFromLocalStorage(): INotification[] {
    try {
      const stored = localStorage.getItem("notifications_list");
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Failed to load notifications from localStorage:", error);
      return [];
    }
  }

  /**
   * Clear all notifications from localStorage
   */
  static clearLocalStorage(): void {
    try {
      localStorage.removeItem("notifications_list");
    } catch (error) {
      console.error("Failed to clear notifications from localStorage:", error);
    }
  }
}

// Export default
export default NotificationService;
